/**
 * Print service — Supabase-backed queue (file kept as `firestorePrintService`
 * for compatibility with existing imports).
 *
 * Flow: Web POS → Supabase `print_jobs` table → LAN relay polls → ESC/POS
 * over raw TCP to the printer → relay updates job status.
 *
 * Tables involved:
 *   - print_jobs           (queue)
 *   - printer_routing      (per-tenant printer registry + category map)
 *   - print_relay_status   (heartbeats)
 */

import { supabase } from './supabaseClient';

export interface PrintJob {
  id?: string;
  order_id: string;
  order_number: string;
  type: 'cashier-receipt' | 'kitchen-order' | 'invoice' | 'report';
  printer_ip: string;
  printer_port: number;
  printer_name: string;
  status: 'pending' | 'printing' | 'printed' | 'failed' | 'cancelled';
  retry_count: number;
  error_message: string | null;
  order_data: any;
  created_at: any;
  updated_at: any;
  printed_at: any;
  created_by: string;
}

export interface PrintJobResult {
  success: boolean;
  jobId: string;
  message: string;
  status: 'pending' | 'printing' | 'printed' | 'failed' | 'cancelled';
}

export interface RelayStatus {
  relay_id: string;
  status: 'online' | 'offline';
  last_heartbeat: any;
  printers: {
    [ip: string]: {
      name: string;
      online: boolean;
      last_check: any;
    };
  };
  version: string;
}

type PrintJobInsert = {
  tenant_id?: string;
  type: PrintJob['type'];
  order_id: string;
  order_number: string;
  order_data: any;
  printer_ip: string;
  printer_port: number;
  printer_name: string;
  status: PrintJob['status'];
  retry_count: number;
  error_message: string | null;
  dedup_key: string;
  created_by: string;
};

export class FirestorePrintService {
  private static instance: FirestorePrintService;

  private static _routingConfig: any = null;
  private static _routingConfigLoadedAt: number = 0;
  private static ROUTING_CACHE_TTL = 60000;

  static DEFAULT_PRINTERS = [
    { id: 'printer1', ip: '192.168.178.42', name: 'Sushi Kitchen', type: 'kitchen' },
    { id: 'printer2', ip: '192.168.178.43', name: 'Pizza Kitchen', type: 'kitchen' },
    { id: 'printer3', ip: '192.168.178.45', name: 'Starters Station', type: 'kitchen' },
    { id: 'printer4', ip: '192.168.178.46', name: 'Cash Register (Epson)', type: 'cashier' },
  ];

  static DEFAULT_CATEGORY_ROUTING: Record<string, string> = {
    'sushi': 'printer1',
    'sashimi': 'printer1',
    'nigiri': 'printer1',
    'maki': 'printer1',
    'uramaki': 'printer1',
    'inside out': 'printer1',
    'inside-out special': 'printer1',
    'crispy roll': 'printer1',
    'temaki': 'printer1',
    'sushi set': 'printer1',
    'sushi boxen': 'printer1',
    'sushi specials': 'printer1',
    'pizza': 'printer2',
    'pizza & calzone': 'printer2',
    'calzone': 'printer2',
    'pasta': 'printer2',
    'klassiker': 'printer2',
    'vegetarisch': 'printer2',
    'spezialitäten': 'printer2',
    'fleisch': 'printer2',
    'fisch & meeresfrüchte': 'printer2',
    'bowls': 'printer3',
    'bowl': 'printer3',
    'poke bowls': 'printer3',
    'poke bowl': 'printer3',
    'starters': 'printer3',
    'vorspeisen': 'printer3',
    'extras': 'printer3',
    'sides': 'printer3',
    'appetizers': 'printer3',
    'insalata': 'printer3',
    'salad': 'printer3',
    'suppen': 'printer3',
    'soups': 'printer3',
    'miso soup': 'printer3',
    'desserts': 'printer3',
    'drinks': 'printer4',
    'getränke': 'printer4',
    'getränk': 'printer4',
    'beverages': 'printer4',
    'green tea': 'printer4',
  };

  private statusChannel: ReturnType<typeof supabase.channel> | null = null;
  private jobChannels: Map<string, ReturnType<typeof supabase.channel>> = new Map();

  private constructor() {
    console.log('[PRINT SERVICE] Supabase Print Service initialized');
  }

  public static getInstance(): FirestorePrintService {
    if (!FirestorePrintService.instance) {
      FirestorePrintService.instance = new FirestorePrintService();
    }
    return FirestorePrintService.instance;
  }

  private async currentUserEmail(): Promise<string> {
    const { data } = await supabase.auth.getSession();
    return data.session?.user?.email || 'POS System';
  }

  public async createPrintJob(
    orderId: string,
    orderNumber: string,
    orderData: any,
    type: 'cashier-receipt' | 'kitchen-order' | 'invoice' | 'report',
    printerIp: string,
    printerName: string,
    options?: { dedupKey?: string; skipDedup?: boolean }
  ): Promise<PrintJobResult> {
    try {
      const createdBy = await this.currentUserEmail();
      const dedupKey = options?.dedupKey || `${orderId}_${type}_${printerIp}`;

      if (!options?.skipDedup) {
        const { data: existing } = await supabase
          .from('print_jobs')
          .select('id, status')
          .eq('dedup_key', dedupKey)
          .limit(1)
          .maybeSingle();
        if (existing) {
          console.log(`[PRINT SERVICE] Duplicate prevented: ${type} for ${orderNumber} on ${printerName} (key=${dedupKey})`);
          return {
            success: true,
            jobId: existing.id,
            message: 'Print job already exists (duplicate prevented)',
            status: existing.status as PrintJob['status'],
          };
        }
      }

      const cleanOrderData = JSON.parse(JSON.stringify(orderData));
      if (orderNumber && !cleanOrderData.orderNumber) {
        cleanOrderData.orderNumber = orderNumber;
      }

      const insert: PrintJobInsert = {
        type,
        order_id: orderId,
        order_number: orderNumber,
        order_data: cleanOrderData,
        printer_ip: printerIp,
        printer_port: 9100,
        printer_name: printerName,
        status: 'pending',
        retry_count: 0,
        error_message: null,
        dedup_key: dedupKey,
        created_by: createdBy,
      };

      console.log(`[PRINT SERVICE] Creating ${type} job for ${printerName} (${printerIp})`);

      const { data, error } = await supabase
        .from('print_jobs')
        .insert(insert)
        .select('id')
        .single();

      if (error) throw error;

      console.log(`[PRINT SERVICE] Print job created: ${data.id}`);

      return {
        success: true,
        jobId: data.id,
        message: `Print job created for ${printerName}`,
        status: 'pending',
      };
    } catch (error) {
      console.error('[PRINT SERVICE] Failed to create print job:', error);
      return {
        success: false,
        jobId: '',
        message: `Failed to create print job: ${(error as Error).message}`,
        status: 'failed',
      };
    }
  }

  public async printCashierReceipt(
    orderId: string,
    orderNumber: string,
    orderData: any
  ): Promise<PrintJobResult[]> {
    const items = orderData.items || [];
    const total = orderData.total || 0;
    if (items.length === 0 || total <= 0) {
      console.log('[PRINT SERVICE] Skipping cashier receipt — empty items or zero total');
      return [];
    }

    const config = await this.loadRoutingConfig();
    const cashPrinters = (config.printers as any[]).filter((p) => p.type === 'cashier');
    const targets = cashPrinters.length > 0
      ? cashPrinters.map((p: any) => ({ ip: p.ip, name: p.name }))
      : [{ ip: '192.168.178.46', name: 'Cash Register (Epson)' }];

    const results = await Promise.all(
      targets.map((printer: any) =>
        this.createPrintJob(orderId, orderNumber, orderData, 'cashier-receipt', printer.ip, printer.name)
      )
    );

    return results;
  }

  public async loadRoutingConfig(): Promise<{ printers: any[]; categoryRouting: Record<string, string[]> }> {
    const now = Date.now();
    if (
      FirestorePrintService._routingConfig &&
      now - FirestorePrintService._routingConfigLoadedAt < FirestorePrintService.ROUTING_CACHE_TTL
    ) {
      return FirestorePrintService._routingConfig;
    }

    const defaultsAsArrays: Record<string, string[]> = {};
    for (const [k, v] of Object.entries(FirestorePrintService.DEFAULT_CATEGORY_ROUTING)) {
      defaultsAsArrays[k] = [v];
    }

    try {
      const { data, error } = await supabase
        .from('printer_routing')
        .select('printers, category_routing')
        .limit(1)
        .maybeSingle();

      if (!error && data) {
        const rawRouting: any = data.category_routing || {};
        const arrayRouting: Record<string, string[]> = {};
        for (const [cat, val] of Object.entries(rawRouting)) {
          if (Array.isArray(val) && val.length > 0) {
            arrayRouting[cat] = (val as string[]).filter(Boolean);
          } else if (typeof val === 'string' && val) {
            arrayRouting[cat] = [val];
          }
        }
        const mergedRouting = { ...defaultsAsArrays, ...arrayRouting };
        const config = {
          printers: (data.printers as any[]) || FirestorePrintService.DEFAULT_PRINTERS,
          categoryRouting: mergedRouting,
        };
        FirestorePrintService._routingConfig = config;
        FirestorePrintService._routingConfigLoadedAt = now;
        console.log('[PRINT SERVICE] Routing config loaded from Supabase:', Object.keys(arrayRouting).length, 'categories');
        return config;
      }
    } catch (err) {
      console.warn('[PRINT SERVICE] Failed to load routing config, using defaults:', err);
    }

    const fallback = {
      printers: FirestorePrintService.DEFAULT_PRINTERS,
      categoryRouting: defaultsAsArrays,
    };
    FirestorePrintService._routingConfig = fallback;
    FirestorePrintService._routingConfigLoadedAt = now;
    return fallback;
  }

  public classifyItemToPrinters(
    item: any,
    categoryRouting: Record<string, string[] | string>
  ): string[] {
    if (item._printerId) return [item._printerId];
    if (item.printer_id) return [item.printer_id];

    const toArr = (v: string[] | string | undefined): string[] => {
      if (!v) return [];
      return Array.isArray(v) ? v.filter(Boolean) : [v];
    };

    const category = (item.category_name || item.category || '').toLowerCase().trim();
    const itemName = (item.name || item.menu_item_name || '').toLowerCase().trim();

    if (category) {
      for (const [key, val] of Object.entries(categoryRouting)) {
        if (key.toLowerCase().trim() === category) {
          const ids = toArr(val);
          if (ids.length > 0) return ids;
        }
      }
    }

    const pizzaKeywords = ['pizza', 'calzone', 'pasta'];
    if (pizzaKeywords.some(kw => itemName.includes(kw) || category.includes(kw))) {
      return ['printer2'];
    }
    const bowlKeywords = ['bowl', 'poke', 'açaí', 'acai', 'salat', 'salad', 'insalata'];
    if (bowlKeywords.some(kw => itemName.includes(kw) || category.includes(kw))) {
      return ['printer3'];
    }
    const starterKeywords = ['starter', 'vorspeise', 'extra', 'suppe', 'soup', 'dessert'];
    if (starterKeywords.some(kw => itemName.includes(kw) || category.includes(kw))) {
      return ['printer3'];
    }
    const drinkKeywords = ['drink', 'getränk', 'beverage', 'cola', 'fanta', 'sprite', 'wasser', 'water', 'juice', 'saft', 'bier', 'beer', 'wine', 'wein', 'tea', 'tee', 'coffee', 'kaffee', 'latte', 'cappuccino', 'espresso', 'lemonade', 'limonade'];
    if (drinkKeywords.some(kw => itemName.includes(kw) || category.includes(kw))) {
      return ['printer4'];
    }

    return ['printer1'];
  }

  public async printKitchenOrder(
    orderId: string,
    orderNumber: string,
    orderData: any
  ): Promise<PrintJobResult[]> {
    const items = orderData.items || [];
    if (items.length === 0) {
      console.log('[PRINT SERVICE] Skipping kitchen order — no items');
      return [];
    }

    const config = await this.loadRoutingConfig();
    const printerMap = new Map<string, any[]>();

    items.forEach((item: any) => {
      const printerIds = this.classifyItemToPrinters(item, config.categoryRouting);
      const printerId = printerIds[0] || 'printer1';
      if (!printerMap.has(printerId)) printerMap.set(printerId, []);
      printerMap.get(printerId)!.push(item);
    });

    if (printerMap.size === 0) {
      console.log('[PRINT SERVICE] No category match — routing to printer1 (default)');
      const defaultPrinter = (config.printers as any[])[0] || FirestorePrintService.DEFAULT_PRINTERS[0];
      const result = await this.createPrintJob(
        orderId, orderNumber, orderData,
        'kitchen-order', defaultPrinter.ip, defaultPrinter.name
      );
      return [result];
    }

    const printerLookup = new Map((config.printers as any[]).map((p) => [p.id, p]));
    const allPrinterIds = Array.from(printerMap.keys());
    const results: PrintJobResult[] = [];

    for (const [printerId, printerItems] of Array.from(printerMap.entries())) {
      const printer = printerLookup.get(printerId) || FirestorePrintService.DEFAULT_PRINTERS[0];
      const otherStationItems = allPrinterIds
        .filter(pid => pid !== printerId)
        .flatMap(pid => (printerMap.get(pid) || []).map((it: any) => ({
          name: it.name,
          quantity: it.quantity || 1,
          _station: (printerLookup.get(pid) as any)?.name || pid,
        })));

      const priorityItems = printerItems.map((it: any) => ({ ...it, _priority: true }));
      const secondaryItems = otherStationItems.map((it: any) => ({
        ...it,
        _priority: false,
        category_name: it._station || it.category_name || '',
      }));
      const mergedItems = [...priorityItems, ...secondaryItems];

      const stationOrderData = {
        ...orderData,
        items: mergedItems,
        _otherStationItems: otherStationItems,
        _stationName: printer.name,
        _printerType: printer.type || 'kitchen',
      };

      const result = await this.createPrintJob(
        orderId, orderNumber, stationOrderData,
        'kitchen-order', printer.ip, printer.name
      );
      results.push(result);
    }

    return results;
  }

  public async printKitchenAddition(
    orderId: string,
    orderNumber: string,
    orderData: any,
    newItemsOnly: any[],
    additionRoundId: string
  ): Promise<PrintJobResult[]> {
    const results: PrintJobResult[] = [];
    if (!newItemsOnly || newItemsOnly.length === 0) {
      console.log('[PRINT SERVICE] Skipping addition — no new items');
      return results;
    }

    const config = await this.loadRoutingConfig();
    const printerMap = new Map<string, any[]>();
    newItemsOnly.forEach((item: any) => {
      const ids = this.classifyItemToPrinters(item, config.categoryRouting);
      const pid = ids[0] || 'printer1';
      if (!printerMap.has(pid)) printerMap.set(pid, []);
      printerMap.get(pid)!.push(item);
    });

    if (printerMap.size === 0) {
      printerMap.set('printer1', [...newItemsOnly]);
    }

    const printerLookup = new Map((config.printers as any[]).map((p) => [p.id, p]));

    for (const [printerId, stationItems] of Array.from(printerMap.entries())) {
      const printer = printerLookup.get(printerId) || FirestorePrintService.DEFAULT_PRINTERS[0];
      const priorityItems = stationItems.map((it: any) => ({ ...it, _priority: true }));

      const stationOrderData = {
        ...orderData,
        items: priorityItems,
        _stationName: printer.name,
        _printerType: printer.type || 'kitchen',
        _isAddition: true,
        _additionRoundId: additionRoundId,
        addition_header: `+ ZUSÄTZLICHE ARTIKEL (Runde ${additionRoundId.split('_').pop() || ''})`,
      };

      const result = await this.createPrintJob(
        orderId, orderNumber, stationOrderData,
        'kitchen-order', printer.ip, printer.name,
        { dedupKey: `${orderId}_add_${additionRoundId}_${printer.ip}` }
      );
      results.push(result);
    }
    return results;
  }

  public async printOrder(
    orderId: string,
    orderNumber: string,
    orderData: any
  ): Promise<{ receipt: PrintJobResult[]; kitchen: PrintJobResult[] }> {
    const [receipt, kitchen] = await Promise.all([
      this.printCashierReceipt(orderId, orderNumber, orderData),
      this.printKitchenOrder(orderId, orderNumber, orderData),
    ]);
    return { receipt, kitchen };
  }

  public async printInvoice(
    orderId: string,
    orderNumber: string,
    orderData: any,
    printerIp?: string,
    printerName?: string
  ): Promise<PrintJobResult> {
    const config = await this.loadRoutingConfig();
    const cashPrinter = (config.printers as any[]).find((p) => p.type === 'cashier')
      || FirestorePrintService.DEFAULT_PRINTERS[3];
    return this.createPrintJob(
      orderId,
      orderNumber,
      orderData,
      'invoice',
      printerIp || cashPrinter.ip,
      printerName || cashPrinter.name,
    );
  }

  public async printReport(
    reportId: string,
    reportLabel: string,
    reportData: any,
    printerIp?: string,
    printerName?: string,
  ): Promise<PrintJobResult> {
    const config = await this.loadRoutingConfig();
    const cashPrinter = (config.printers as any[]).find((p) => p.type === 'cashier')
      || FirestorePrintService.DEFAULT_PRINTERS[3];
    return this.createPrintJob(
      reportId,
      reportLabel,
      reportData,
      'report',
      printerIp || cashPrinter.ip,
      printerName || cashPrinter.name,
      { dedupKey: `report_${reportId}_${Date.now()}`, skipDedup: false },
    );
  }

  public async reprintOrder(
    orderId: string,
    orderNumber: string,
    orderData: any,
    type?: 'cashier-receipt' | 'kitchen-order' | 'both'
  ): Promise<PrintJobResult[]> {
    const results: PrintJobResult[] = [];
    const reprintData = { ...orderData, isCopy: true };
    const stamp = Date.now();

    if (type === 'cashier-receipt' || type === 'both' || !type) {
      const config = await this.loadRoutingConfig();
      const cashPrinters = (config.printers as any[]).filter((p) => p.type === 'cashier');
      const targets = cashPrinters.length > 0
        ? cashPrinters
        : [FirestorePrintService.DEFAULT_PRINTERS[3]];
      const receipts = await Promise.all(
        targets.map((printer: any) =>
          this.createPrintJob(orderId, orderNumber, reprintData,
            'cashier-receipt', printer.ip, printer.name,
            { dedupKey: `${orderId}_reprint_receipt_${stamp}_${printer.ip}` })
        )
      );
      results.push(...receipts);
    }

    if (type === 'kitchen-order' || type === 'both' || !type) {
      // Reprint goes through the standard router but with a fresh dedup key
      // (handled by passing _reprintRound via orderData if needed).
      const kitchen = await this.printKitchenOrder(orderId, orderNumber, {
        ...reprintData,
        _reprintRound: stamp,
      });
      results.push(...kitchen);
    }

    return results;
  }

  public onPrintJobStatusChange(
    jobId: string,
    callback: (status: PrintJob['status'], errorMessage?: string | null) => void
  ): () => void {
    const channelName = `print_job_${jobId}`;
    const existing = this.jobChannels.get(channelName);
    if (existing) {
      void supabase.removeChannel(existing);
      this.jobChannels.delete(channelName);
    }

    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'print_jobs', filter: `id=eq.${jobId}` },
        (payload: any) => {
          const row = payload.new as Partial<PrintJob>;
          if (row && row.status) callback(row.status, row.error_message ?? null);
        }
      )
      .subscribe();

    this.jobChannels.set(channelName, channel);

    // Also fire one initial check in case the job already terminated by the time we subscribed.
    void supabase
      .from('print_jobs')
      .select('status, error_message')
      .eq('id', jobId)
      .maybeSingle()
      .then(({ data }) => {
        if (data && (data.status === 'printed' || data.status === 'failed')) {
          callback(data.status as PrintJob['status'], data.error_message ?? null);
        }
      });

    return () => {
      const ch = this.jobChannels.get(channelName);
      if (ch) {
        void supabase.removeChannel(ch);
        this.jobChannels.delete(channelName);
      }
    };
  }

  public waitForPrintJob(jobId: string, timeoutMs: number = 30000): Promise<PrintJobResult> {
    return new Promise((resolve) => {
      let resolved = false;

      const timeout = setTimeout(() => {
        if (!resolved) {
          resolved = true;
          unsubscribe();
          resolve({
            success: false,
            jobId,
            message: 'Print job timed out — relay may be offline',
            status: 'pending',
          });
        }
      }, timeoutMs);

      const unsubscribe = this.onPrintJobStatusChange(jobId, (status, errorMessage) => {
        if (!resolved && (status === 'printed' || status === 'failed')) {
          resolved = true;
          clearTimeout(timeout);
          unsubscribe();
          resolve({
            success: status === 'printed',
            jobId,
            message: status === 'printed' ? 'Printed successfully' : (errorMessage || 'Print failed'),
            status,
          });
        }
      });
    });
  }

  public onRelayStatusChange(
    callback: (status: RelayStatus | null) => void
  ): () => void {
    if (this.statusChannel) {
      void supabase.removeChannel(this.statusChannel);
      this.statusChannel = null;
    }

    const evaluate = async () => {
      const { data } = await supabase
        .from('print_relay_status')
        .select('*')
        .order('last_heartbeat', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (!data) {
        callback(null);
        return;
      }
      const status = { ...data } as any;
      const lastBeat = status.last_heartbeat ? new Date(status.last_heartbeat) : null;
      if (lastBeat && Date.now() - lastBeat.getTime() > 120_000) {
        status.status = 'offline';
      }
      callback(status as RelayStatus);
    };

    this.statusChannel = supabase
      .channel('print_relay_status_changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'print_relay_status' }, () => {
        void evaluate();
      })
      .subscribe();

    void evaluate();

    return () => {
      if (this.statusChannel) {
        void supabase.removeChannel(this.statusChannel);
        this.statusChannel = null;
      }
    };
  }

  public onRecentPrintJobs(
    callback: (jobs: PrintJob[]) => void,
    limitCount: number = 20
  ): () => void {
    const channelName = 'print_jobs_recent';

    const evaluate = async () => {
      const { data } = await supabase
        .from('print_jobs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(limitCount);
      callback((data || []) as PrintJob[]);
    };

    const channel = supabase
      .channel(channelName)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'print_jobs' }, () => {
        void evaluate();
      })
      .subscribe();

    void evaluate();

    return () => {
      void supabase.removeChannel(channel);
    };
  }
}

export const firestorePrintService = FirestorePrintService.getInstance();
export default FirestorePrintService;
